import { useState, useEffect } from "react";
import { useAppSelector } from "../../../../../store/types";
import SbuxButtonGroup, {
  type ButtonItem,
} from "../../../../../components/SbuxButtonGroup";
import { Box, FormControl, InputLabel } from "@mui/material";
import SbuxTextField from "../../../../../components/SubxTextField";
import SbuxTranslate from "../../../../../components/SbuxTranslate";
import React from "react";
import SbuxMediaRecorder from "../../../../../components/SbuxMediaRecorder";
import MediaMgtFileUpload from "./MediaMgtFileUpload";
import styles from "../../styles";
import { MEDIAMGT_FILE_NAME_ERROR } from "../../../../../constants";
import MediaTextToSpeech from "./MediaTextToSpeech";
import { getTextLengthErrorMessage } from "../../utils";
import useCss from "../../../../../hooks/useCss";
import { getMediaMgtRowsSelector } from "../../../../../selectors/mediaMgtSelector";
import type { MediaInfo } from "../../common/types";

interface MediaRow {
  languageCode?: string;
  language?: string;
  promptName?: string;
  description?: string;
  fileName?: string;
  [key: string]: unknown;
}

interface MediaMgtDataRow {
  promptName?: string;
  [key: string]: unknown;
}

interface Props {
  row: MediaRow;
  mediaContent?: string | null;
  handleMediaMgtRow: (row: MediaRow, hasError?: boolean) => void;
  handleMediaInfo: (info: MediaInfo | null) => void;
  buttonList: ButtonItem[];
  handleDisableSaveBtn: (disabled: boolean) => void;
}

const MediaMgtEdit = ({
  row,
  mediaContent,
  handleMediaMgtRow,
  handleMediaInfo,
  buttonList,
  handleDisableSaveBtn,
}: Props) => {
  const classes = useCss(styles);

  const mediaMgtRows = useAppSelector(getMediaMgtRowsSelector);

  const [rowObj, setRowObj] = useState<MediaRow | null>(null);
  const [fileName, setFileName] = useState(row.promptName ?? "");
  const [promptName, setPromptName] = useState(row.promptName ?? "");
  const [description, setDescription] = useState(row.description ?? "");
  const [toggleButton, setToggleButton] = useState<number>(1);
  const [fileNameError, setFileNameError] = useState(false);
  const [searchRowData, setSearchRowData] = useState<MediaMgtDataRow[]>([]);
  const [fileNameLengthError, setFileNameLengthError] = useState(false);
  const [descriptionLengthError, setDescriptionLengthError] = useState(false);

  useEffect(() => {
    setSearchRowData((mediaMgtRows ?? []) as MediaMgtDataRow[]);
    setRowObj(row);
  }, [mediaMgtRows]);

  const handleToggle = (newValue: string) => {
    setToggleButton(Number(newValue));
  };

  const handleFileNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setFileName(value);
    row.fileName = value;
    handleMediaMgtRow(row);
  };

  const handlePromptChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (
      rowObj &&
      rowObj.promptName?.toLowerCase() !== value.toLowerCase() &&
      searchRowData.some(
        (r) => r.promptName?.toLowerCase() === value.toLowerCase(),
      )
    ) {
      setFileNameError(true);
      row.promptName = "";
    } else {
      setFileNameError(false);
      row.promptName = value;
    }

    let isError = false;
    if (value.length > 50) {
      isError = true;
    }
    setFileNameLengthError(isError);

    setPromptName(value);
    handleMediaMgtRow(row, isError || descriptionLengthError);
  };

  const handleDescriptionChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = event.target.value;
    let isError = false;
    if (value.length > 150) {
      isError = true;
    }
    setDescriptionLengthError(isError);
    setDescription(value);
    row.description = value;
    handleMediaMgtRow(row, isError || fileNameLengthError);
  };

  const getErrorMessage = () => {
    if (fileNameError) {
      return MEDIAMGT_FILE_NAME_ERROR;
    }
    if (fileNameLengthError) {
      return getTextLengthErrorMessage(50);
    }
  };

  return (
    <Box>
      <Box
        component="form"
        className={classes.formControl}
        noValidate
        autoComplete="off"
      >
        <Box>
          <SbuxTextField
            variant={"outlined"}
            disabled
            preventAutoFocus
            label={<SbuxTranslate>{`Language`}</SbuxTranslate>}
            value={row.language ?? ""}
          />
        </Box>
        <Box>
          <SbuxTextField
            variant={"outlined"}
            disabled
            preventAutoFocus
            label={<SbuxTranslate>{`Current File`}</SbuxTranslate>}
            value={fileName}
            handleChange={handleFileNameChange}
          />
        </Box>
        <Box className={classes.t2sAudioPlayer}>
          <audio controls title={row.fileName}>
            <source src={mediaContent || undefined} type="audio/wav" />
          </audio>
        </Box>
        <Box className="fullWidth">
          <FormControl>
            <InputLabel>
              <SbuxTranslate>{`Step 2: Upload or record a file`}</SbuxTranslate>
            </InputLabel>

            <Box className={classes.mediaDialogTabs}>
              <SbuxButtonGroup
                buttonList={buttonList}
                value={String(toggleButton)}
                handleButtonToggle={handleToggle}
              />
              <Box
                className={`tabContainer ${
                  toggleButton === 3 ? "textToSpeech" : "uploadRecord"
                }`}
              >
                {toggleButton === 1 && (
                  <MediaMgtFileUpload
                    row={row}
                    handleMediaInfo={handleMediaInfo}
                  />
                )}
                {toggleButton === 2 && (
                  <SbuxMediaRecorder
                    handleMediaInfo={
                      handleMediaInfo as (
                        info: Parameters<
                          React.ComponentProps<
                            typeof SbuxMediaRecorder
                          >["handleMediaInfo"]
                        >[0],
                      ) => void
                    }
                  />
                )}
                {toggleButton === 3 && (
                  <MediaTextToSpeech
                    selectedLanguageCode={row.languageCode}
                    handleMediaInfo={handleMediaInfo}
                    handleDisableSaveBtn={handleDisableSaveBtn}
                  />
                )}
              </Box>
            </Box>
          </FormControl>
        </Box>
        <Box>
          <SbuxTextField
            label={<SbuxTranslate>{`File Name`}</SbuxTranslate>}
            value={promptName}
            errorMessage={getErrorMessage()}
            maxLength={50}
            isError={fileNameError || fileNameLengthError}
            error={fileNameError || fileNameLengthError}
            displayCounter={true}
            handleChange={handlePromptChange}
          />
        </Box>
        <Box>
          <SbuxTextField
            label={<SbuxTranslate>{`Description (Optional)`}</SbuxTranslate>}
            value={description}
            handleChange={handleDescriptionChange}
            errorMessage={getTextLengthErrorMessage(150)}
            isError={descriptionLengthError}
            error={descriptionLengthError}
            maxLength={150}
            displayCounter={true}
            preventAutoFocus
          />
        </Box>
      </Box>
    </Box>
  );
};

export default MediaMgtEdit;
